import { KeyCodes } from '../../../utils/keyCodes';
import { useCallback, useEffect, useImperativeHandle, useState } from 'react';

export const useCheckbox = ({
  disabled,
  readOnly,
  onChange,
  onBlur,
  field,
  ref,
  inputNode,
  checkStatus,
  defaultChecked,
  onKeyInput,
}) => {
  const status =
    defaultChecked || (field && field.checked ? field.checked : checkStatus);

  const [isChecked, setIsChecked] = useState(status);
  const [keyFocused, setKeyFocused] = useState(false);

  useEffect(() => {
    setIsChecked(status);
  }, [status]);

  const handleBlur = () => {
    onBlur && onBlur();
    setKeyFocused(false);
  };

  const stopPropagation = (event) => {
    event.stopPropagation();
  };

  const toggle = useCallback(
    (e) => {
      if (disabled || readOnly) return;
      setIsChecked(!isChecked);
      if (!e.detail) onKeyInput(e);
    },
    [
      isChecked,
      setIsChecked,
      field,
      onChange,
      inputNode,
      disabled,
      readOnly,
      onKeyInput,
    ],
  );

  const handleChange = useCallback(
    (event) => {
      if (inputNode.current === null || disabled || readOnly) return;
      const outsideHandleChange = (field && field.onChange) || onChange;
      if (outsideHandleChange) {
        outsideHandleChange(isChecked);
      }
    },
    [inputNode, disabled, readOnly, onChange, toggle, field],
  );

  const handleKeyControl = useCallback(
    (event) => {
      !keyFocused && setKeyFocused(true);
      switch (event.keyCode) {
        case KeyCodes.enter:
        case KeyCodes.space:
          toggle(event);
          break;
        default:
          return;
      }
      event.stopPropagation();
      event.preventDefault();
    },
    [field, KeyCodes, isChecked, keyFocused],
  );

  useImperativeHandle(ref, () => ({
    item: inputNode.current,
    focus: () => {
      if (inputNode) {
        inputNode.current.focus();
      }
    },
  }));

  return {
    toggle,
    handleChange,
    handleKeyControl,
    isChecked,
    keyFocused,
    handleBlur,
    stopPropagation,
  };
};
